/**
 * sn-geschenk — Geschenk-Automatik + Stueckzahl-Logik fuer den Warenkorb (Horizon). Markup: snippets/sn-geschenk.liquid,
 * snippets/cart-products.liquid (Stepper je Produkt, Hinweiszeile).
 *
 * Geschenk (26.09.2026): Ab 49 € Warenwert (ohne Geschenk) liegt das gewaehlte Geschenk gratis im Korb, darunter fliegt es
 * raus. Auswahl-Wechsel tauscht die Geschenkzeile. Wer das Geschenk per X entfernt, bekommt es in dieser Sitzung nicht
 * automatisch zurueck (bis der Warenwert wieder unter 49 € faellt) — „+ Gratis dazulegen“ holt es jederzeit zurueck.
 *
 * Stueckzahl (18.10.2026, Befund E11, Ken): Die Menge im Warenkorb zaehlt DOSEN bzw. SETS, nicht Pakete.
 * - Je Produkt EIN Stepper (erste Zeile des Produkts, cart-products.liquid zeigt dort die Gesamtzahl). Plus/Minus
 *   aendert die Gesamtzahl um 1; der Korb wird danach in die guenstigste Paketkombination zerlegt
 *   (groesstes Paket zuerst, Rest kleiner): 2 Sets → 3 Sets, 3 Dosen → 3 Dosen + 1 Dose, 5 → 5er, 6 → 5er + 1.
 *   Horizons eigene Mengenaenderung wird fuer solche Zeilen abgefangen (sonst 2 × „2 Sets“ = 4 Sets, Befund 18.10.).
 * - Dieselbe Zerlegung laeuft nach jeder Korb-Aenderung (z. B. zweimal „1 Set“ von der Produktseite → „2 Sets“).
 * - Nach einer Umstellung kurzer Hinweis im Einschub („… du sparst 8,00 €“).
 *
 * Alle Korb-Aenderungen laufen nacheinander durch eine Warteschlange (reihe); eine angestossene Pruefung geht nie
 * verloren. Wir aendern den Korb per AJAX-API und melden das Horizon ueber dasselbe Ereignis wie der Kaufen-Knopf
 * (shopify:cart:lines-update); Horizon rendert Einschub, Warenkorbseite und Zaehler dann selbst neu.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const GIFTS = [39859707805872, 39859707936944, 46870515974475, 46870470295883, 39859719766192, 39859713441968];
const THRESHOLD = 4900;
const OFF_KEY = 'snGiftOff';
const MAX_EINHEITEN = 99;

/** Alle Korb-Aenderungen nacheinander, nie parallel. */
let kette = Promise.resolve();
let pruefungAngemeldet = false;
function reihe(fn) {
  const p = kette.then(fn, fn);
  kette = p.catch(() => {});
  return p;
}
function pruefungAnstossen() {
  if (pruefungAngemeldet) return;
  pruefungAngemeldet = true;
  reihe(async () => {
    pruefungAngemeldet = false;
    await check();
  });
}

const getOff = () => {
  try { return sessionStorage.getItem(OFF_KEY) === '1'; } catch { return false; }
};
const setOff = (on) => {
  try { on ? sessionStorage.setItem(OFF_KEY, '1') : sessionStorage.removeItem(OFF_KEY); } catch {}
};

const isGift = (item) => Boolean(item && item.properties && item.properties._sn_gift);
/** „3 Dosen“ → 3, „2 Sets“ → 2, „1 Dose“ → 1, „Default Title“ → 0 (kein Paketprodukt). */
const einheiten = (t) => parseInt(String(t || '').trim(), 10) || 0;
const istPaketZeile = (item) => !isGift(item) && !item.selling_plan_allocation && einheiten(item.variant_title) > 0;

/** Alle Warenkorb-Abschnitte auf der Seite (Einschub + ggf. Warenkorbseite). */
function sectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

function eventTarget() {
  return document.querySelector('theme-drawer#cart-drawer') || document;
}

async function getCart() {
  const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { credentials: 'same-origin' });
  return res.json();
}

/**
 * Fuehrt eine Korb-Aenderung aus und laesst Horizon neu rendern.
 * @param {'add'|'change'|'update'} kind
 * @param {object} body
 * @param {Array<{merchandiseId: string, quantity: number}>} lines
 */
async function mutate(kind, body, lines) {
  const sections = sectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();
  eventTarget().dispatchEvent(
    new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines, promise: deferred.promise })
  );
  try {
    const url = `${window.Shopify?.routes?.root || '/'}cart/${kind}.js`;
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(sections.length ? { ...body, sections: sections.join(','), sections_url: location.pathname } : body),
    });
    const data = await res.json();
    const cart = await getCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { sections: data.sections, items: cart.items, source: 'sn-geschenk', didError: !res.ok },
    });
    return cart;
  } catch (error) {
    deferred.resolve({ detail: { didError: true, source: 'sn-geschenk' } });
    throw error;
  }
}

/** Variante aus dem Dropdown; ohne Dropdown das erste Geschenk, das nicht schon als normale Zeile im Korb liegt. */
function selectedGift(cart) {
  const sel = /** @type {HTMLSelectElement | null} */ (document.querySelector('[data-sn-wahl-select]'));
  if (sel) {
    const id = Number(sel.value);
    if (GIFTS.includes(id)) return id;
  }
  const imKorb = new Set((cart?.items || []).filter((i) => !isGift(i)).map((i) => i.variant_id));
  return GIFTS.find((id) => !imKorb.has(id)) ?? GIFTS[0];
}

const addGift = (id) =>
  mutate('add', { items: [{ id, quantity: 1, properties: { _sn_gift: '1' } }] }, [
    { merchandiseId: String(id), quantity: 1 },
  ]);

const removeLine = (line) =>
  mutate('change', { id: line.key, quantity: 0 }, [{ merchandiseId: String(line.variant_id), quantity: 0 }]);

/* ---------- Stueckzahl: Pakete je Produkt ---------- */

/** Paketstufen eines Produkts aus /products/<handle>.js (einmal je Seite geladen). */
const stufenCache = new Map();
async function stufen(handle) {
  if (stufenCache.has(handle)) return stufenCache.get(handle);
  const p = (async () => {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`, { credentials: 'same-origin' });
    if (!res.ok) return [];
    const produkt = await res.json();
    return (produkt.variants || [])
      .map((v) => ({ id: v.id, n: einheiten(v.title), da: v.available, preis: Number(v.price) || 0, titel: v.title }))
      .filter((v) => v.n > 0 && v.da)
      .sort((a, b) => b.n - a.n);
  })();
  stufenCache.set(handle, p);
  return p;
}

/** Zerlegt eine Stueckzahl in Pakete: groesstes zuerst, Rest kleiner. null, wenn es nicht aufgeht (kein 1er verfuegbar). */
function zerlege(anzahl, liste) {
  const teile = [];
  let rest = anzahl;
  for (const s of liste) {
    const k = Math.floor(rest / s.n);
    if (k > 0) {
      teile.push({ id: s.id, n: s.n, qty: k, titel: s.titel, preis: s.preis });
      rest -= k * s.n;
    }
  }
  return rest === 0 ? teile : null;
}

const einheitWort = (titel, anzahl, en) => {
  const set = /set/i.test(String(titel || ''));
  if (en) return set ? (anzahl === 1 ? 'set' : 'sets') : (anzahl === 1 ? 'tub' : 'tubs');
  return set ? (anzahl === 1 ? 'Set' : 'Sets') : (anzahl === 1 ? 'Dose' : 'Dosen');
};

/**
 * Bringt die Paketzeilen jedes Produkts in die guenstigste Kombination. ziele: { product_id: gewuenschte Stueckzahl }
 * (vom Stepper); ohne Ziel bleibt die Stueckzahl, nur die Aufteilung wird bereinigt (z. B. 2 × „1 Set“ → „2 Sets“).
 * @returns {Promise<boolean>} true, wenn der Korb geaendert wurde
 */
async function normalisieren(cart, ziele = {}) {
  const gruppen = new Map();
  for (const item of cart.items || []) {
    if (!istPaketZeile(item)) continue;
    const g = gruppen.get(item.product_id) || { handle: item.handle, zeilen: [], anzahl: 0, titel: item.variant_title };
    g.zeilen.push(item);
    g.anzahl += einheiten(item.variant_title) * item.quantity;
    gruppen.set(item.product_id, g);
  }
  let geaendert = false;
  for (const [pid, g] of gruppen) {
    const ziel = ziele[pid] != null ? Math.max(0, Math.min(MAX_EINHEITEN, ziele[pid])) : g.anzahl;
    const liste = await stufen(g.handle);
    if (!liste.length) continue;
    const soll = ziel > 0 ? zerlege(ziel, liste) : [];
    if (soll === null) continue;
    // Ist-Zustand schon richtig? (eine Zeile je Paketstufe, richtige Mengen)
    const sollMap = new Map(soll.map((s) => [s.id, s.qty]));
    let gleich = g.zeilen.length === soll.length;
    if (gleich) for (const z of g.zeilen) if (sollMap.get(z.variant_id) !== z.quantity) { gleich = false; break; }
    if (gleich) continue;
    const updates = {};
    const belegt = new Set();
    for (const z of g.zeilen) {
      const q = sollMap.get(z.variant_id);
      if (q != null && !belegt.has(z.variant_id)) { updates[z.key] = q; belegt.add(z.variant_id); }
      else updates[z.key] = 0;
    }
    for (const [id, q] of sollMap) if (!belegt.has(id)) updates[id] = q;
    await mutate('update', { updates }, soll.map((s) => ({ merchandiseId: String(s.id), quantity: s.qty })));
    geaendert = true;
    if (ziel > 0) zeigePaketHinweis(ziel, soll, liste, g.titel);
  }
  return geaendert;
}

/* ---------- Hinweis im Einschub ---------- */

/** Kurzer Hinweis oben im Einschub (9 s). Horizon zeichnet den ganzen Einschub-Abschnitt nach jeder Korb-Aenderung neu
 *  (morphSection) und wirft fremde Elemente dabei raus – ein Beobachter setzt den Hinweis so lange wieder ein. */
let paketToast = null;
let toastWaechter = null;
function toastEinsetzen() {
  if (!paketToast) return;
  if (Date.now() > paketToast.until) {
    paketToast = null;
    document.querySelectorAll('.sn-paket-toast').forEach((e) => e.remove());
    return;
  }
  if (document.querySelector('.sn-paket-toast')) return;
  const host = document.querySelector('theme-drawer#cart-drawer .cart-drawer__items, theme-drawer#cart-drawer cart-items-component, cart-items-component');
  if (!host) return;
  const el = document.createElement('p');
  el.className = 'sn-paket-toast';
  el.setAttribute('role', 'status');
  el.textContent = paketToast.text;
  host.prepend(el);
  setTimeout(toastEinsetzen, paketToast.until - Date.now() + 50);
}
function zeigePaketHinweis(anzahl, soll, liste, titel) {
  const en = (document.documentElement.lang || '').toLowerCase().startsWith('en');
  const einzel = liste.find((s) => s.n === 1);
  const paketPreis = soll.reduce((sum, s) => sum + s.qty * s.preis, 0);
  const ersparnis = einzel ? anzahl * einzel.preis - paketPreis : 0;
  if (!soll.some((s) => s.n > 1) || ersparnis <= 0) return;
  let betrag = '';
  try {
    betrag = new Intl.NumberFormat(en ? 'en-GB' : 'de-DE', { style: 'currency', currency: window.Shopify?.currency?.active || 'EUR' }).format(ersparnis / 100);
  } catch { betrag = (ersparnis / 100).toFixed(2) + ' €'; }
  const teile = soll.map((s) => (s.qty > 1 ? `${s.qty} × ` : '') + s.titel).join(' + ');
  const wort = einheitWort(titel, anzahl, en);
  const kopf = `${anzahl} ${wort}`;
  const als = soll.length === 1 && soll[0].qty === 1 ? '' : (en ? ` as ${teile}` : ` als ${teile}`);
  const text = en
    ? `${kopf}${als} – you save ${betrag} compared to single ${einheitWort(titel, 2, en)}.`
    : `${kopf}${als} – du sparst ${betrag} gegenüber ${/set/i.test(titel) ? 'einzelnen Sets' : 'Einzeldosen'}.`;
  document.querySelectorAll('.sn-paket-toast').forEach((e) => e.remove());
  paketToast = { text, until: Date.now() + 9000 };
  setTimeout(toastEinsetzen, 350);
  if (!toastWaechter) {
    toastWaechter = new MutationObserver(() => { if (paketToast) toastEinsetzen(); });
    toastWaechter.observe(document.getElementById('shopify-section-cart-drawer-section') || document.body, { childList: true, subtree: true });
  }
}

/* ---------- Geschenk ---------- */

async function geschenkPruefen(cart) {
  let qual = 0;
  let giftLine = null;
  for (const item of cart.items || []) {
    if (isGift(item)) giftLine = item;
    else qual += item.final_line_price;
  }
  if (qual < THRESHOLD && getOff()) setOff(false);
  if (qual >= THRESHOLD && !giftLine && !getOff()) {
    await addGift(selectedGift(cart));
  } else if (qual < THRESHOLD && giftLine) {
    await removeLine(giftLine);
  }
}

/** Laeuft immer innerhalb der Warteschlange (reihe). */
async function check(ziele) {
  try {
    let cart = await getCart();
    if (await normalisieren(cart, ziele || {})) cart = await getCart();
    await geschenkPruefen(cart);
  } catch {
    // Netzfehler: beim naechsten Korb-Ereignis erneut versuchen
  }
}

/** Geschenk im Korb gegen die Dropdown-Wahl tauschen (laeuft in der Warteschlange). */
async function tausche(id) {
  try {
    const cart = await getCart();
    const giftLine = (cart.items || []).find(isGift);
    if (giftLine && giftLine.variant_id !== id) {
      await mutate('change', { id: giftLine.key, quantity: 0 }, [
        { merchandiseId: String(giftLine.variant_id), quantity: 0 },
      ]);
      await addGift(id);
    }
  } catch {
    // naechste Korb-Aenderung prueft erneut
  }
}

/* ---------- Ereignisse ---------- */

// Stepper an einer Paketzeile im Korb: Horizons Mengenaenderung abfangen (sie wuerde nur die EINE Zeile
// vervielfachen, z. B. 2 × „2 Sets“), stattdessen Stueckzahl des Produkts setzen und neu zerlegen.
// Capture-Phase am document: laeuft vor Horizons cart-items-Komponente; stopPropagation haelt sie fern.
document.addEventListener(
  'quantity-selector:update',
  (event) => {
    const d = /** @type {any} */ (event).detail || {};
    if (!d.cartLine) return; // Produktseite → sn-kaufboxen
    const row = event.target instanceof Element ? event.target.closest('[data-key]') : null;
    if (!(row instanceof HTMLElement) || row.classList.contains('sn-geschenk-zeile')) return;
    const variante = row.querySelector('.cart-items__variants dd')?.textContent || '';
    if (!einheiten(variante)) return; // kein Paketprodukt → Horizon macht es selbst
    event.stopImmediatePropagation();
    const key = row.dataset.key;
    const ziel = Math.max(0, Math.min(MAX_EINHEITEN, Math.round(Number(d.quantity) || 0)));
    reihe(async () => {
      try {
        const cart = await getCart();
        const item = (cart.items || []).find((i) => i.key === key);
        if (!item || !istPaketZeile(item)) return;
        await check({ [item.product_id]: ziel });
      } catch {
        // naechste Korb-Aenderung prueft erneut
      }
    });
  },
  true
);

// Jede andere Korb-Aenderung (Kaufen-Knopf, Entfernen, Kaufleiste) -> danach pruefen. Nie verwerfen.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const e = /** @type {any} */ (event);
  const done = e.promise && typeof e.promise.then === 'function' ? e.promise : Promise.resolve();
  done
    .then((result) => {
      if (result?.detail?.source === 'sn-geschenk') return;
      setTimeout(pruefungAnstossen, 120);
    })
    .catch(() => setTimeout(pruefungAnstossen, 400));
});

// Auswahl gewechselt: Bild sofort tauschen, Geschenkzeile ersetzen (eingereiht, geht nicht verloren).
document.addEventListener('change', (event) => {
  const sel = event.target;
  if (!(sel instanceof HTMLSelectElement) || !sel.matches('[data-sn-wahl-select]')) return;
  const id = Number(sel.value);
  if (!GIFTS.includes(id)) return;
  const box = sel.closest('[data-sn-wahl]');
  const img = box?.querySelector('[data-sn-wahl-bild]');
  const src = box?.getAttribute(`data-img-${id}`);
  if (img instanceof HTMLImageElement && src) img.src = src;
  reihe(() => tausche(id));
});

// „+ Gratis dazulegen“ und X an der Geschenkzeile
document.addEventListener(
  'click',
  (event) => {
    const t = event.target instanceof Element ? event.target : null;
    if (!t) return;
    if (t.closest('[data-sn-wahl-dazu]')) {
      setOff(false);
      reihe(async () => {
        const cart = await getCart();
        if ((cart.items || []).some(isGift)) return;
        await addGift(selectedGift(cart));
      });
      return;
    }
    if (t.closest('.sn-geschenk-zeile .cart-items__remove')) setOff(true);
  },
  true
);

// Beim Laden einmal pruefen (z. B. Korb aus einer frueheren Sitzung).
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', pruefungAnstossen);
else pruefungAnstossen();

// Kassenfix 20.10.2026: Im Stueckzahl-Modus zeigt das Mengenfeld im Warenkorb Dosen bzw. Sets statt der Paketmenge
// (3er-Paket = „3“). Horizon schickt diese Felder (updates[]) beim Klick auf „Zur Kasse“ mit dem Formular an /cart,
// Shopify setzte damit die Paketmenge auf die Dosenzahl (aus einem 3er-Paket wurden drei 3er-Pakete).
// Alle Mengen aendern wir sofort per AJAX, der Korb ist also immer aktuell. Deshalb gehen beim Abschicken keine Mengenfelder mit.
{
  const istKorbFormular = (f) => {
    if (!(f instanceof HTMLFormElement)) return false;
    if (f.id === 'cart-form') return true;
    try {
      return /\/cart\/?$/.test(new URL(f.getAttribute('action') || '', location.href).pathname);
    } catch (e) {
      return false;
    }
  };
  const istMengenFeld = (name) => name === 'updates[]' || /^updates\[/.test(name || '');
  document.addEventListener(
    'submit',
    (event) => {
      const f = event.target;
      if (!istKorbFormular(f)) return;
      const felder = [...f.querySelectorAll('input[name]')];
      if (f.id) felder.push(...document.querySelectorAll(`input[form="${f.id}"][name]`));
      const weg = felder.filter((el) => istMengenFeld(el.getAttribute('name')));
      weg.forEach((el) => {
        el.dataset.snName = el.getAttribute('name');
        el.removeAttribute('name');
      });
      setTimeout(() => {
        weg.forEach((el) => {
          if (el.dataset.snName) {
            el.setAttribute('name', el.dataset.snName);
            delete el.dataset.snName;
          }
        });
      }, 3000);
    },
    true
  );
  document.addEventListener(
    'formdata',
    (event) => {
      if (!istKorbFormular(event.target)) return;
      for (const k of [...event.formData.keys()]) if (istMengenFeld(k)) event.formData.delete(k);
    },
    true
  );
}
